import { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ExcelJS from 'exceljs';
import ChartCard from './ChartCard';
import DataTable from './DataTable';
import ErrorMessage from './ErrorMessage';
import StatCard from './StatCard';
import LoadingState from './LoadingState';
import { useActiveUpload } from '../contexts/ActiveUploadContext';
import { useDateRange } from '../contexts/DateRangeContext';
import { getClientSummary, getDashboardSummary, getStateSummary, getTeamMemberDetailRows, getTeamMemberPerformance } from '../services/reportService';
import { getTatSettings } from '../services/settingsService';
import { buildTeamContributionSummary, groupTeamPerformanceRows } from '../utils/analytics';
import { formatNumber, formatPercent } from '../utils/formatters';
import { MILESTONE_COLUMNS, ROLE_DEFINITIONS } from '../utils/constants';
import { normalizeServiceType, trimText } from '../utils/normalization';
import { exportSheetsToXlsx } from '../utils/exporters';
import {
  buildRolePerformanceSections as buildIndividualRolePerformanceSections,
  buildIndividualKpiSummary,
  assignmentRowsForMember as buildIndividualAssignmentRows,
} from '../utils/individualPerformance';

const DEFAULT_SELECTION = {
  dashboard: true,
  team: true,
  individual: false,
  internalTeam: false,
  stateClient: true,
};

const PRIMARY_MILESTONES = ['Initial Project Delivery', 'Restamp Required'];
const milestoneTypeSet = new Set(MILESTONE_COLUMNS.map((column) => column.milestoneType));
const DESIGN_ROLE = ROLE_DEFINITIONS.find((role) => role.key === 'design_engineer') || ROLE_DEFINITIONS[0];
const PE_ROLE = ROLE_DEFINITIONS.find((role) => role.key === 'pe_engineer') || ROLE_DEFINITIONS[1];


const INTERNAL_TEAM_GROUPS = [
  {
    role: 'PE I',
    members: [
      { display: 'Sonia Joy Mukundwa', aliases: ['Sonia Joy Mukundwa'] },
      { display: 'Kevin Manzi', aliases: ['Kevin Manzi'] },
      { display: 'Peace Umutoni', aliases: ['Peace Umutoni'] },
      { display: 'Aimee M. Ineza Hirwa', aliases: ['Aimee M. Ineza Hirwa', 'Aimee Mireille Ineza Hirwa'] },
      { display: 'Tresor Rurengera', aliases: ['Tresor Rurengera'] },
      { display: 'Josiane Uwabera', aliases: ['Josiane Uwabera'] },
      { display: 'Audrey B. Mudendeli', aliases: ['Audrey B. Mudendeli', 'Audrey Bruce Mudendeli'] },
      { display: 'Olivier Manzi', aliases: ['Olivier Manzi'] },
      { display: 'Desire Nzubahimana', aliases: ['Desire Nzubahimana'] },
    ],
  },
  {
    role: 'PE II',
    members: [
      { display: 'Cedrick Ishimwe', aliases: ['Cedrick Ishimwe'] },
      { display: 'Deborah C. Mbabazi', aliases: ['Deborah C. Mbabazi', 'Deborah Mbabazi'] },
      { display: 'Emmanuel Kwizera', aliases: ['Emmanuel Kwizera'] },
      { display: 'Dieudonne Numugabo', aliases: ['Dieudonne Numugabo', 'Dieudonne NUMUGABO'] },
      { display: 'Aksa Numuhamya', aliases: ['Aksa Numuhamya'] },
      { display: 'Davide Irumva', aliases: ['Davide Irumva'] },
      { display: 'Enock Rukundo', aliases: ['Enock Rukundo'] },
      { display: 'Stella Munyana', aliases: ['Stella Munyana'] },
      { display: 'Marie T. Niyomwungeri', aliases: ['Marie T. Niyomwungeri', 'Marie Therese Niyomwungeri'] },
      { display: 'Pacifique Hirwa', aliases: ['Pacifique Hirwa', 'Hirwa Pacifique'] },
      { display: 'Julienne Mbabazi', aliases: ['Julienne Mbabazi'] },
      { display: 'Emmanuel Ndatimana', aliases: ['Emmanuel Ndatimana'] },
      { display: 'Zadock Uwimanikunda', aliases: ['Zadock Uwimanikunda'] },
    ],
  },
  {
    role: 'SPVD',
    members: [
      { display: 'Eric PV Solar Design Ruta', aliases: ['Eric PV Solar Design Ruta', 'Eric Ruta'] },
      { display: 'John A. Villanueva', aliases: ['John A. Villanueva', 'John Anthony Villanueva'] },
      { display: 'Roberto Abanto Jr', aliases: ['Roberto Abanto Jr', 'Roberto Jr Abanto', 'Robi Abanto Jr'] },
      { display: 'Ma. Daniela Antonio', aliases: ['Ma. Daniela Antonio'] },
    ],
  },
];

const countColumn = { key: 'project_count', label: 'Project Count', align: 'right', render: formatNumber };
const percentColumn = { key: 'percent_of_total', label: '% of Total', align: 'right', render: (value) => formatPercent(value) };
const milestoneSeries = MILESTONE_COLUMNS.map((column) => ({ key: column.key, name: column.label }));
const milestoneColumns = [
  { key: 'member_name', label: 'Team Member' },
  ...MILESTONE_COLUMNS.map((column) => ({ key: column.key, label: column.label, align: 'right' })),
  { key: 'total', label: 'Total', align: 'right' },
];
const tatColumns = [
  { key: 'service_type', label: 'Service Type' },
  { key: 'promised_tat_hours', label: 'Promised TAT', align: 'right', render: (value) => Number(value || 0).toFixed(2) },
  countColumn,
  { key: 'on_time', label: 'On-Time', align: 'right', render: formatNumber },
  { key: 'late', label: 'Late', align: 'right', render: formatNumber },
  { key: 'on_time_percent', label: 'On-Time %', align: 'right', render: (value) => formatPercent(value) },
];
const statusColumns = [
  { key: 'status', label: 'Status' },
  countColumn,
  percentColumn,
];
const onTimeSummaryColumns = [
  { key: 'milestone_type', label: 'Milestone Type' },
  { key: 'on_time', label: 'On-Time', align: 'right', render: formatNumber },
  { key: 'late', label: 'Late', align: 'right', render: formatNumber },
  { key: 'total', label: 'Total', align: 'right', render: formatNumber },
  { key: 'on_time_percent', label: 'On-Time %', align: 'right', render: formatPercent },
];

const qualityColumns = [
  { key: 'milestone_type', label: 'Milestone Type' },
  { key: 'total_count', label: 'Total Initial + Restamp', align: 'right', render: formatNumber },
  { key: 'no_error_count', label: 'No Error', align: 'right', render: formatNumber },
  { key: 'error_count', label: 'Error / Other', align: 'right', render: formatNumber },
  { key: 'blank_count', label: 'Blank', align: 'right', render: formatNumber },
  { key: 'no_error_percent', label: 'No Error %', align: 'right', render: formatPercent },
];

const assignmentColumns = [
  { key: 'project_name', label: 'Project' },
  { key: 'milestone_date', label: 'Date' },
  { key: 'milestone_type', label: 'Milestone Type' },
  { key: 'assigned_role', label: 'Assigned Role' },
  { key: 'service_type', label: 'Service Type' },
  { key: 'tat_hours', label: 'TAT', align: 'right', render: (value) => value === null || value === undefined ? '—' : Number(value).toFixed(2) },
  { key: 'target_tat_label', label: 'Target TAT', align: 'right' },
  { key: 'qa_result', label: 'QA Result' },
  { key: 'cre_restamp_reason', label: 'CRE Restamp Reason' },
  { key: 'status', label: 'Role TAT Status' },
];

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function asNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function splitMemberNames(value) {
  return trimText(value)
    .split('&')
    .map((name) => trimText(name))
    .filter(Boolean);
}

function memberKey(value) {
  return trimText(value).toLowerCase();
}

function roleFieldValue(row, role) {
  return trimText(row?.[role.normField] || '');
}

function rowMatchesMemberForRole(row, role, selectedMember) {
  if (!selectedMember) return false;
  const selected = memberKey(selectedMember);
  return splitMemberNames(roleFieldValue(row, role)).some((name) => memberKey(name) === selected);
}

function rowMatchesMemberAnyRole(row, selectedMember) {
  return ROLE_DEFINITIONS.some((role) => rowMatchesMemberForRole(row, role, selectedMember));
}

function milestoneKey(milestoneType) {
  if (milestoneType === 'Initial Project Delivery') return 'initial_project_delivery';
  if (milestoneType === 'Restamp Required') return 'restamp_required';
  if (milestoneType === 'Post Install Letter') return 'post_install_letter';
  return 'other';
}

function serviceLabel(row) {
  return normalizeServiceType(row?.service_type_norm || row?.service_type) || 'Unassigned';
}

function promisedTatForRow(row, tatSettings) {
  const label = serviceLabel(row);
  return asNumber(tatSettings?.serviceTatHours?.[label] ?? tatSettings?.defaultTatHours ?? 24);
}

function isOnTime(row, tatSettings) {
  const totalTat = asNumber(row?.total_tat_hours);
  return totalTat > 0 && totalTat <= promisedTatForRow(row, tatSettings);
}

function qaResult(row) {
  return trimText(row?.milestone_qa_result_norm || row?.milestone_qa_result || rawValueByHeaders(row, [
    'Milestone QA Result',
    'AU',
  ]));
}

function isNoError(row) {
  return qaResult(row).replace(/[\s_-]+/g, '').toLowerCase() === 'noerror';
}

function isBlankQa(row) {
  return !qaResult(row);
}

function makePercentRows(rows, valueKey = 'project_count') {
  const total = rows.reduce((sum, row) => sum + asNumber(row[valueKey]), 0);
  return rows.map((row) => ({
    ...row,
    percent_of_total: total ? Math.round((asNumber(row[valueKey]) * 1000) / total) / 10 : 0,
  }));
}

function sortByCountThenName(a, b, countKey, nameKey) {
  return asNumber(b[countKey]) - asNumber(a[countKey]) || String(a[nameKey] || '').localeCompare(String(b[nameKey] || ''), undefined, { sensitivity: 'base' });
}

function countBy(rows, getKey, nameKey) {
  const map = new Map();
  rows.forEach((row) => {
    const key = trimText(getKey(row)) || 'Unassigned';
    map.set(key, (map.get(key) || 0) + 1);
  });
  return makePercentRows(
    [...map.entries()]
      .map(([key, count]) => ({ [nameKey]: key, project_count: count }))
      .sort((a, b) => sortByCountThenName(a, b, 'project_count', nameKey)),
  );
}

function buildMemberOptions(rows) {
  const map = new Map();
  rows.forEach((row) => {
    ROLE_DEFINITIONS.forEach((role) => {
      splitMemberNames(roleFieldValue(row, role)).forEach((name) => {
        const key = memberKey(name);
        if (key && !map.has(key)) map.set(key, name);
      });
    });
  });
  return [...map.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function roleRowsForMember(rows, selectedMember) {
  return ROLE_DEFINITIONS.map((role) => {
    const matchedRows = rows.filter((row) => rowMatchesMemberForRole(row, role, selectedMember));
    const current = {
      role_key: role.key,
      role_label: role.label,
      initial_project_delivery: 0,
      restamp_required: 0,
      post_install_letter: 0,
      other: 0,
      total: 0,
      matchedRows,
    };
    matchedRows.forEach((row) => {
      const key = milestoneKey(row.milestone_type);
      current[key] += 1;
      current.total += 1;
    });
    return current;
  });
}

function buildOnTimeStatusRows(rows, milestoneType, tatSettings) {
  const scopedRows = rows.filter((row) => row.milestone_type === milestoneType);
  const onTime = scopedRows.filter((row) => isOnTime(row, tatSettings)).length;
  const late = scopedRows.length - onTime;
  return makePercentRows([
    { status: 'On-Time', project_count: onTime },
    { status: 'Late', project_count: late },
  ]);
}

function buildOnTimeByMilestoneRows(rows, tatSettings) {
  return PRIMARY_MILESTONES.map((milestoneType) => {
    const scopedRows = rows.filter((row) => row.milestone_type === milestoneType);
    const onTime = scopedRows.filter((row) => isOnTime(row, tatSettings)).length;
    const late = scopedRows.length - onTime;
    return {
      milestone_type: milestoneType,
      on_time: onTime,
      late,
      total: scopedRows.length,
      on_time_percent: scopedRows.length ? Math.round((onTime * 1000) / scopedRows.length) / 10 : 0,
    };
  });
}

function buildQualityRows(rows) {
  const summarize = (scopedRows) => {
    const noError = scopedRows.filter(isNoError).length;
    const blank = scopedRows.filter(isBlankQa).length;
    const total = scopedRows.length;
    return {
      total_count: total,
      no_error_count: noError,
      error_count: Math.max(0, total - noError - blank),
      blank_count: blank,
      no_error_percent: total ? Math.round((noError * 1000) / total) / 10 : 0,
    };
  };

  const milestoneRows = PRIMARY_MILESTONES.map((milestoneType) => ({
    milestone_type: milestoneType,
    ...summarize(rows.filter((row) => row.milestone_type === milestoneType)),
  }));
  return [
    ...milestoneRows,
    {
      milestone_type: 'Total Initial + Restamp',
      ...summarize(rows.filter((row) => PRIMARY_MILESTONES.includes(row.milestone_type))),
    },
  ];
}

function buildQualityPieRows(qualityRow) {
  if (!qualityRow || asNumber(qualityRow.total_count) <= 0) return [];
  return makePercentRows([
    { status: 'No Error', project_count: asNumber(qualityRow.no_error_count) },
    { status: 'Error / Other', project_count: asNumber(qualityRow.error_count) },
    { status: 'Blank', project_count: asNumber(qualityRow.blank_count) },
  ]).filter((row) => asNumber(row.project_count) > 0);
}

function buildInHouseExternalRows(rows) {
  const map = new Map();
  rows.filter((row) => PRIMARY_MILESTONES.includes(row.milestone_type)).forEach((row) => {
    const source = trimText(row.in_house_vs_external) || 'Unassigned';
    const current = map.get(source) || {
      in_house_vs_external: source,
      initial_project_delivery: 0,
      restamp_required: 0,
    };
    if (row.milestone_type === 'Initial Project Delivery') current.initial_project_delivery += 1;
    if (row.milestone_type === 'Restamp Required') current.restamp_required += 1;
    map.set(source, current);
  });
  return [...map.values()].map((row) => ({
    ...row,
    revision_rate: row.initial_project_delivery ? Math.round((row.restamp_required * 1000) / row.initial_project_delivery) / 10 : 0,
  })).sort((a, b) => {
    const order = { 'In House': 1, External: 2, Unassigned: 3 };
    return (order[a.in_house_vs_external] || 99) - (order[b.in_house_vs_external] || 99) || a.in_house_vs_external.localeCompare(b.in_house_vs_external);
  });
}

function buildIdleRows(rows) {
  return PRIMARY_MILESTONES.map((milestoneType) => {
    const scopedRows = rows.filter((row) => row.milestone_type === milestoneType);
    const readyForDesign = scopedRows.map((row) => asNumber(row.ready_for_design_tat_hours)).filter((value) => value > 0);
    const readyForPe = scopedRows.map((row) => asNumber(row.ready_for_pe_tat_hours)).filter((value) => value > 0);
    const average = (values) => (values.length ? Math.round((values.reduce((sum, value) => sum + value, 0) / values.length) * 100) / 100 : 0);
    return {
      milestone_type: milestoneType,
      avg_ready_for_design: average(readyForDesign),
      avg_ready_for_pe: average(readyForPe),
      request_count: scopedRows.length,
    };
  });
}

function uniqueProjectCount(rows) {
  return new Set(rows.map((row) => row.project_key || row.project_name || row.id).filter(Boolean)).size;
}

function rowsHavePositiveValue(rows, keys = ['project_count']) {
  return (rows || []).some((row) => keys.some((key) => asNumber(row?.[key]) > 0));
}

function rawValueByHeaders(row, headers = []) {
  const raw = row?.raw_row_data;
  if (!raw || typeof raw !== 'object') return null;

  for (const header of headers) {
    if (raw[header] !== undefined && raw[header] !== null && raw[header] !== '') return raw[header];
  }

  const normalizedTargets = headers.map((header) => String(header || '').replace(/[\s_:\-–—/]+/g, '').toLowerCase());
  const found = Object.entries(raw).find(([key, value]) => {
    if (value === undefined || value === null || value === '') return false;
    const normalizedKey = String(key || '').replace(/[\s_:\-–—/]+/g, '').toLowerCase();
    return normalizedTargets.includes(normalizedKey) || normalizedTargets.some((target) => normalizedKey.endsWith(target));
  });
  return found ? found[1] : null;
}

function buildAssignmentPreviewRows(rows, selectedMember) {
  return buildIndividualAssignmentRows(rows || [], selectedMember, 500);
}



function aliasKeys(member) {
  return [member.display, ...(member.aliases || [])]
    .map((name) => memberKey(name))
    .filter(Boolean);
}

function resolveInternalMemberName(rows, member) {
  const options = buildMemberOptions(rows || []);
  const optionMap = new Map(options.map((name) => [memberKey(name), name]));
  for (const key of aliasKeys(member)) {
    if (optionMap.has(key)) return optionMap.get(key);
  }
  return member.display;
}

function formatKpiValue(item) {
  if (!item) return 'N/A';
  if (item.format === 'hoursPerDay') return item.value === null || item.value === undefined ? 'N/A' : `${item.value} h/day`;
  if (item.format === 'percent') return item.value === null || item.value === undefined ? 'N/A' : formatPercent(item.value);
  return item.value ?? 'N/A';
}

function compactAssignmentRows(rows, teamMemberName = '', teamRole = '') {
  return (rows || []).map((row) => ({
    team_member_name: teamMemberName,
    role: teamRole,
    project_name: row.project_name || '',
    milestone_date: row.milestone_date || '',
    milestone_type: row.milestone_type || '',
    assigned_role: row.assigned_role || '',
    service_type: row.service_type || '',
    tat_hours: row.tat_hours,
    target_tat_label: row.target_tat_label || '',
    qa_result: row.qa_result || '',
    cre_restamp_reason: row.cre_restamp_reason || '',
    status: row.status || '',
  }));
}


function buildInternalMemberReportRows(rows) {
  return INTERNAL_TEAM_GROUPS.map((group) => ({
    ...group,
    members: group.members.map((member) => {
      const resolvedName = resolveInternalMemberName(rows, member);
      const roleSections = buildIndividualRolePerformanceSections(rows || [], resolvedName);
      const kpiSummary = buildIndividualKpiSummary(roleSections);
      const assignments = compactAssignmentRows(buildAssignmentPreviewRows(rows || [], resolvedName), member.display, group.role);
      return {
        ...member,
        resolvedName,
        matched: assignments.length > 0,
        kpiSummary,
        assignments,
      };
    }),
  }));
}

function normalizeStatusRows(rows) {
  const mapped = new Map((rows || []).map((row) => [row.status, { ...row, project_count: Number(row.project_count || 0) }]));
  return ['On-Time', 'Late'].map((status) => mapped.get(status) || { status, project_count: 0, percent_of_total: 0 });
}

function qualityStatusRows(rows = [], milestoneType) {
  const source = rows.find((row) => row.milestone_type === milestoneType) || {};
  const noError = Number(source.no_error_count || 0);
  const blank = Number(source.blank_count || 0);
  const errorOther = Number(source.error_count || 0);
  const total = noError + errorOther + blank;

  return [
    {
      status: 'No Error',
      project_count: noError,
      percent_of_total: total ? Math.round((noError * 1000) / total) / 10 : 0,
    },
    {
      status: 'Error / Other',
      project_count: errorOther,
      percent_of_total: total ? Math.round((errorOther * 1000) / total) / 10 : 0,
    },
    {
      status: 'Blank',
      project_count: blank,
      percent_of_total: total ? Math.round((blank * 1000) / total) / 10 : 0,
    },
  ].filter((row) => row.project_count > 0 || total === 0);
}

function kpiSelection(summary) {
  const wanted = ['initial_delivered', 'rework_delivered', 'initial_on_time_percent', 'rework_on_time_percent', 'avg_ready_for_design'];
  const kpis = summary?.kpis || [];
  return wanted.map((key) => kpis.find((item) => item.key === key)).filter(Boolean);
}

function roleChartHeight(rowCount) {
  return Math.max(320, Math.min(900, rowCount * 30 + 180));
}

function categoryChartHeight(rowCount) {
  return Math.max(320, Math.min(1100, rowCount * 23 + 160));
}

function PdfDashboardSection({ summary }) {
  const initialOnTimeStatus = normalizeStatusRows(summary?.initialOnTimeStatus || summary?.onTimeStatus || []);
  const reworkOnTimeStatus = normalizeStatusRows(summary?.reworkOnTimeStatus || []);
  const initialByService = summary?.initialByService || [];
  const reworkByService = summary?.reworkByService || [];
  const idleRows = summary?.idleTimeSummary || [];
  const inHouseRows = summary?.inHouseExternalSummary || [];
  const designQualityMilestoneRows = summary?.designQualityByMilestone || [];
  const initialDesignQualityStatus = qualityStatusRows(designQualityMilestoneRows, 'Initial Project Delivery');
  const reworkDesignQualityStatus = qualityStatusRows(designQualityMilestoneRows, 'Restamp Required');

  return (
    <section className="pdf-section pdf-dashboard-section">
      <h2>Dashboard</h2>
      <div className="pdf-kpi-row">
        {kpiSelection(summary).map((item) => <StatCard key={item.key} item={item} />)}
      </div>

      <div className="pdf-grid-two pdf-service-grid">
        <div className="pdf-workbook-block">
          <div className="section-bar">Projects Delivered by Service</div>
          <ChartCard title="Projects Delivered by Service" data={initialByService} xKey="service_type" type="donut" nameKey="service_type" valueKey="project_count" height={520} showLegend minCalloutPercent={0.03} maxCallouts={3} />
          <DataTable columns={[{ key: 'service_type', label: 'Service Type' }, countColumn, percentColumn]} rows={initialByService} />
        </div>
        <div className="pdf-workbook-block">
          <div className="section-bar">Rework Delivered by Service</div>
          <ChartCard title="Rework Delivered by Service" data={reworkByService} xKey="service_type" type="donut" nameKey="service_type" valueKey="project_count" height={520} showLegend minCalloutPercent={0.03} maxCallouts={3} />
          <DataTable columns={[{ key: 'service_type', label: 'Service Type' }, countColumn, percentColumn]} rows={reworkByService} />
        </div>
      </div>

      <div className="pdf-workbook-block">
        <div className="section-bar">Internal Design Quality — Milestone QA Result</div>
        <div className="pdf-grid-two">
          <div>
            <ChartCard title="Initial Project Delivery: Internal Design Quality" data={initialDesignQualityStatus} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={320} showLegend={false} minCalloutPercent={0.01} maxCallouts={3} />
            <DataTable columns={statusColumns} rows={initialDesignQualityStatus} />
          </div>
          <div>
            <ChartCard title="Restamp Required: Internal Design Quality" data={reworkDesignQualityStatus} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={320} showLegend={false} minCalloutPercent={0.01} maxCallouts={3} />
            <DataTable columns={statusColumns} rows={reworkDesignQualityStatus} />
          </div>
        </div>
      </div>

      <div className="pdf-grid-two">
        <div className="pdf-workbook-block">
          <ChartCard title="Initial Project Delivery: On-Time vs Late" data={initialOnTimeStatus} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={320} showLegend={false} minCalloutPercent={0.01} maxCallouts={4} />
          <DataTable columns={[{ key: 'status', label: 'Status' }, countColumn, percentColumn]} rows={initialOnTimeStatus} />
          <DataTable columns={tatColumns} rows={summary?.initialOnTimeByService || []} />
        </div>
        <div className="pdf-workbook-block">
          <ChartCard title="Restamp Required: On-Time vs Late" data={reworkOnTimeStatus} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={320} showLegend={false} minCalloutPercent={0.01} maxCallouts={4} />
          <DataTable columns={[{ key: 'status', label: 'Status' }, countColumn, percentColumn]} rows={reworkOnTimeStatus} />
          <DataTable columns={tatColumns} rows={summary?.reworkOnTimeByService || []} />
        </div>
      </div>

      <div className="pdf-grid-two">
        <div className="pdf-workbook-block">
          <div className="section-bar">Average Idle Time</div>
          <DataTable
            columns={[
              { key: 'milestone_type', label: 'Milestone Type' },
              { key: 'avg_ready_for_design', label: 'Avg Ready for Design (hrs)', align: 'right', render: (value) => Number(value || 0).toFixed(2) },
              { key: 'avg_ready_for_pe', label: 'Avg Ready for PE (hrs)', align: 'right', render: (value) => Number(value || 0).toFixed(2) },
              { key: 'request_count', label: 'Request Count', align: 'right', render: formatNumber },
            ]}
            rows={idleRows}
          />
          <ChartCard title="Average Idle Time" data={idleRows} xKey="milestone_type" type="groupedBar" series={[{ key: 'avg_ready_for_design', name: 'Avg Ready for Design (hrs)' }, { key: 'avg_ready_for_pe', name: 'Avg Ready for PE (hrs)' }]} height={300} />
        </div>
        <div className="pdf-workbook-block">
          <div className="section-bar">Design Initial Project Delivery vs Restamp Required by In House vs External</div>
          <DataTable
            columns={[
              { key: 'in_house_vs_external', label: 'In House vs External' },
              { key: 'initial_project_delivery', label: 'Initial Project Delivery', align: 'right' },
              { key: 'restamp_required', label: 'Restamp Required', align: 'right' },
              { key: 'revision_rate', label: 'Revision Rate', align: 'right', render: (value) => formatPercent(value) },
            ]}
            rows={inHouseRows}
          />
          <ChartCard title="Design Initial Project Delivery vs Restamp Required by In House vs External" data={inHouseRows} xKey="in_house_vs_external" type="groupedBar" series={[{ key: 'initial_project_delivery', name: 'Initial Project Delivery' }, { key: 'restamp_required', name: 'Restamp Required' }]} height={300} />
        </div>
      </div>

      <div className="pdf-grid-two">
        <div className="pdf-workbook-block">
          <div className="section-bar">Average On-Hold Time by Request Type</div>
          <DataTable columns={[{ key: 'reason', label: 'On-Hold Reason' }, { key: 'avg_on_hold_time', label: 'Avg On-Hold Time', align: 'right', render: (value) => Number(value || 0).toFixed(2) }, { key: 'project_count', label: 'Project Count', align: 'right' }]} rows={summary?.avgOnHoldByReason || []} />
        </div>
        <ChartCard title="Average On-Hold Time by Request Type" data={summary?.avgOnHoldByReason || []} xKey="reason" yKey="avg_on_hold_time" type="horizontalBar" height={categoryChartHeight((summary?.avgOnHoldByReason || []).length)} showLegend={false} />
      </div>

      <div className="pdf-grid-two">
        <div className="pdf-workbook-block">
          <div className="section-bar">CRE Restamp Reason Summary</div>
          <DataTable columns={[{ key: 'reason', label: 'CRE Restamp Reason' }, countColumn, percentColumn]} rows={summary?.creRestampReasonSummary || []} />
          <ChartCard title="CRE Restamp Reason Summary" data={summary?.creRestampReasonSummary || []} xKey="reason" yKey="project_count" type="horizontalBar" height={categoryChartHeight((summary?.creRestampReasonSummary || []).length)} showLegend={false} />
        </div>
        <div className="pdf-workbook-block">
          <div className="section-bar">On-Hold Reason Summary</div>
          <DataTable columns={[{ key: 'reason', label: 'Resolved On-Hold Reason' }, countColumn, percentColumn]} rows={summary?.onHoldReasonSummary || []} />
          <ChartCard title="On-Hold Reason Summary" data={summary?.onHoldReasonSummary || []} xKey="reason" yKey="project_count" type="horizontalBar" height={categoryChartHeight((summary?.onHoldReasonSummary || []).length)} showLegend={false} />
        </div>
      </div>
    </section>
  );
}

function PdfTeamSection({ teamRows }) {
  const grouped = useMemo(() => groupTeamPerformanceRows(teamRows), [teamRows]);
  const contributionRows = useMemo(() => buildTeamContributionSummary(grouped), [grouped]);
  return (
    <section className="pdf-section pdf-team-section">
      <h2>Team Member Performance</h2>
      <div className="pdf-workbook-block">
        <div className="section-bar">Team Members Contribution Summary</div>
        <DataTable
          columns={[
            { key: 'member_name', label: 'Team Member' },
            ...ROLE_DEFINITIONS.map((role) => ({ key: role.key, label: role.label, align: 'right', render: (value) => value || 0 })),
            { key: 'total_contribution', label: 'Total Contribution', align: 'right' },
          ]}
          rows={contributionRows}
        />
      </div>
      {grouped.map((role, index) => (
        <div key={role.key} className="pdf-role-row">
          <div className="section-heading section-bar soft-bar">
            <h3>{index + 1}. Milestones per {role.label}</h3>
            <span>{role.rows.length} team member{role.rows.length === 1 ? '' : 's'}</span>
          </div>
          <div className="pdf-grid-two">
            <div className="pdf-workbook-block">
              <DataTable columns={milestoneColumns} rows={role.rows} />
            </div>
            <ChartCard title={`Milestones per ${role.label}, Stacked by Milestone Type`} data={role.rows} xKey="member_name" type="stackedBar" series={milestoneSeries} height={roleChartHeight(role.rows.length)} showLegend />
          </div>
        </div>
      ))}
    </section>
  );
}

function PdfIndividualTeamMemberSection({ rows, selectedMember, tatSettings }) {
  const roleSummaryRows = useMemo(() => roleRowsForMember(rows, selectedMember), [rows, selectedMember]);
  const nonZeroRoleSummaryRows = useMemo(() => roleSummaryRows.filter((row) => row.total > 0), [roleSummaryRows]);
  const selectedRows = useMemo(() => rows.filter((row) => rowMatchesMemberAnyRole(row, selectedMember)), [rows, selectedMember]);
  const designRows = useMemo(() => rows.filter((row) => rowMatchesMemberForRole(row, DESIGN_ROLE, selectedMember)), [rows, selectedMember]);
  const peRows = useMemo(() => rows.filter((row) => rowMatchesMemberForRole(row, PE_ROLE, selectedMember)), [rows, selectedMember]);
  const milestoneMixRows = useMemo(() => countBy(selectedRows, (row) => milestoneTypeSet.has(row.milestone_type) ? row.milestone_type : 'Other', 'milestone_type'), [selectedRows]);
  const serviceMixRows = useMemo(() => countBy(selectedRows, serviceLabel, 'service_type'), [selectedRows]);
  const designServiceRows = useMemo(() => countBy(designRows, serviceLabel, 'service_type'), [designRows]);
  const peServiceRows = useMemo(() => countBy(peRows, serviceLabel, 'service_type'), [peRows]);
  const designOnTimeRows = useMemo(() => buildOnTimeByMilestoneRows(designRows, tatSettings), [designRows, tatSettings]);
  const designQualityRows = useMemo(() => buildQualityRows(designRows), [designRows]);
  const peOnTimeRows = useMemo(() => buildOnTimeByMilestoneRows(peRows, tatSettings), [peRows, tatSettings]);
  const designInitialStatus = useMemo(() => buildOnTimeStatusRows(designRows, 'Initial Project Delivery', tatSettings), [designRows, tatSettings]);
  const designRestampStatus = useMemo(() => buildOnTimeStatusRows(designRows, 'Restamp Required', tatSettings), [designRows, tatSettings]);
  const peInitialStatus = useMemo(() => buildOnTimeStatusRows(peRows, 'Initial Project Delivery', tatSettings), [peRows, tatSettings]);
  const peRestampStatus = useMemo(() => buildOnTimeStatusRows(peRows, 'Restamp Required', tatSettings), [peRows, tatSettings]);
  const designInHouseRows = useMemo(() => buildInHouseExternalRows(designRows), [designRows]);
  const peInHouseRows = useMemo(() => buildInHouseExternalRows(peRows), [peRows]);
  const idleRows = useMemo(() => buildIdleRows(selectedRows), [selectedRows]);
  const assignmentPreviewRows = useMemo(() => buildAssignmentPreviewRows(rows, selectedMember).slice(0, 500), [rows, selectedMember]);
  const rolePerformanceSections = useMemo(() => buildIndividualRolePerformanceSections(rows, selectedMember), [rows, selectedMember]);
  const individualKpiSummary = useMemo(() => buildIndividualKpiSummary(rolePerformanceSections), [rolePerformanceSections]);

  const designOnTimeRowsWithData = useMemo(() => designOnTimeRows.filter((row) => asNumber(row.total) > 0), [designOnTimeRows]);
  const peOnTimeRowsWithData = useMemo(() => peOnTimeRows.filter((row) => asNumber(row.total) > 0), [peOnTimeRows]);
  const designQualityRowsWithData = useMemo(() => designQualityRows.filter((row) => asNumber(row.total_count) > 0), [designQualityRows]);
  const designQualityChartRows = useMemo(() => designQualityRowsWithData.filter((row) => row.milestone_type !== 'Total Initial + Restamp'), [designQualityRowsWithData]);
  const designInitialQualityPieRows = useMemo(() => buildQualityPieRows(designQualityChartRows.find((row) => row.milestone_type === 'Initial Project Delivery')), [designQualityChartRows]);
  const designRestampQualityPieRows = useMemo(() => buildQualityPieRows(designQualityChartRows.find((row) => row.milestone_type === 'Restamp Required')), [designQualityChartRows]);
  const idleRowsWithData = useMemo(() => idleRows.filter((row) => asNumber(row.request_count) > 0), [idleRows]);
  const showDesignInitialStatus = useMemo(() => rowsHavePositiveValue(designInitialStatus), [designInitialStatus]);
  const showDesignRestampStatus = useMemo(() => rowsHavePositiveValue(designRestampStatus), [designRestampStatus]);
  const showPeInitialStatus = useMemo(() => rowsHavePositiveValue(peInitialStatus), [peInitialStatus]);
  const showPeRestampStatus = useMemo(() => rowsHavePositiveValue(peRestampStatus), [peRestampStatus]);

  const kpis = useMemo(() => {
    const initial = selectedRows.filter((row) => row.milestone_type === 'Initial Project Delivery').length;
    const restamp = selectedRows.filter((row) => row.milestone_type === 'Restamp Required').length;
    const postInstall = selectedRows.filter((row) => row.milestone_type === 'Post Install Letter').length;
    const totalRoleAssignments = nonZeroRoleSummaryRows.reduce((sum, row) => sum + row.total, 0);
    const designQualityTotal = designRows.filter((row) => PRIMARY_MILESTONES.includes(row.milestone_type)).length;
    const designNoError = designRows.filter((row) => PRIMARY_MILESTONES.includes(row.milestone_type) && isNoError(row)).length;
    const designNoErrorPercent = designQualityTotal ? Math.round((designNoError * 1000) / designQualityTotal) / 10 : 0;
    return [
      { key: 'unique_projects', label: 'Unique Projects', value: uniqueProjectCount(selectedRows), format: 'number' },
      { key: 'total_milestones', label: 'Total Milestone Rows', value: selectedRows.length, format: 'number' },
      { key: 'role_assignments', label: 'Role Assignments', value: totalRoleAssignments, format: 'number' },
      { key: 'initial', label: 'Initial Project Delivery', value: initial, format: 'number' },
      { key: 'restamp', label: 'Restamp Required', value: restamp, format: 'number' },
      { key: 'post_install', label: 'Post Install Letter', value: postInstall, format: 'number' },
      { key: 'design_quality', label: 'Design Quality No Error %', value: designNoErrorPercent, format: 'percent' },
    ].filter((item) => asNumber(item.value) > 0);
  }, [selectedRows, nonZeroRoleSummaryRows, designRows]);

  if (!selectedMember) return null;

  return (
    <section className="pdf-section pdf-individual-team-member-section">
      <h2>Individual Team Member Performance — {selectedMember}</h2>
      <div className="pdf-kpi-row individual-stat-grid">
        {kpis.map((item) => <StatCard key={item.key} item={item} />)}
      </div>

      {rolePerformanceSections.length > 0 && (
        <div className="pdf-workbook-block">
          <div className="section-bar">KPI</div>
          <div className="pdf-kpi-row individual-kpi-summary-grid">
            {individualKpiSummary.map((item) => <StatCard key={item.key} item={item} />)}
          </div>
        </div>
      )}

      {selectedRows.length > 0 && (milestoneMixRows.length > 0 || serviceMixRows.length > 0) && (
        <div className="pdf-grid-two">
          {milestoneMixRows.length > 0 && <ChartCard title="Milestone Type Mix" data={milestoneMixRows} xKey="milestone_type" type="donut" nameKey="milestone_type" valueKey="project_count" height={460} showLegend minCalloutPercent={0.02} maxCallouts={3} />}
          {serviceMixRows.length > 0 && <ChartCard title="Service Type Mix" data={serviceMixRows} xKey="service_type" type="donut" nameKey="service_type" valueKey="project_count" height={460} showLegend minCalloutPercent={0.02} maxCallouts={3} />}
        </div>
      )}

      {designRows.length > 0 && (
        <>
          <div className="section-heading section-bar soft-bar">
            <h3>Design Engineer Performance</h3>
            <span>{designRows.length} milestone row{designRows.length === 1 ? '' : 's'}</span>
          </div>
          {(designServiceRows.length > 0 || designOnTimeRowsWithData.length > 0) && (
            <div className="pdf-grid-two">
              {designServiceRows.length > 0 && <ChartCard title="Design Engineer — Service Type Mix" data={designServiceRows} xKey="service_type" type="donut" nameKey="service_type" valueKey="project_count" height={460} showLegend minCalloutPercent={0.02} maxCallouts={3} />}
              {designOnTimeRowsWithData.length > 0 && (
                <div className="pdf-workbook-block">
                  <DataTable columns={onTimeSummaryColumns} rows={designOnTimeRowsWithData} />
                  <ChartCard title="Design Engineer — On-Time vs Late by Milestone" data={designOnTimeRowsWithData} xKey="milestone_type" type="groupedBar" series={[{ key: 'on_time', name: 'On-Time' }, { key: 'late', name: 'Late' }]} height={330} />
                </div>
              )}
            </div>
          )}
          {designQualityRowsWithData.length > 0 && (
            <div className="quality-section-grid pdf-quality-section-grid">
              <div className="pdf-workbook-block quality-table-panel">
                <div className="section-bar">Design Engineer — Quality by Milestone QA Result</div>
                <DataTable columns={qualityColumns} rows={designQualityRowsWithData} />
              </div>
              {(designInitialQualityPieRows.length > 0 || designRestampQualityPieRows.length > 0) && (
                <div className="quality-pie-grid quality-pie-grid-wide">
                  {designInitialQualityPieRows.length > 0 && <ChartCard title="Design Engineer — Initial Project Delivery: Internal Design Quality" data={designInitialQualityPieRows} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={330} showLegend minCalloutPercent={0.01} maxCallouts={3} />}
                  {designRestampQualityPieRows.length > 0 && <ChartCard title="Design Engineer — Restamp Required: Internal Design Quality" data={designRestampQualityPieRows} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={330} showLegend minCalloutPercent={0.01} maxCallouts={3} />}
                </div>
              )}
            </div>
          )}
          {(showDesignInitialStatus || showDesignRestampStatus) && (
            <div className="pdf-grid-two small-chart-grid">
              {showDesignInitialStatus && (
                <div className="pdf-workbook-block">
                  <ChartCard title="Design Engineer — Initial Project Delivery: On-Time vs Late" data={designInitialStatus} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={320} showLegend={false} minCalloutPercent={0.01} maxCallouts={3} />
                  <DataTable columns={statusColumns} rows={designInitialStatus} />
                </div>
              )}
              {showDesignRestampStatus && (
                <div className="pdf-workbook-block">
                  <ChartCard title="Design Engineer — Restamp Required: On-Time vs Late" data={designRestampStatus} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={320} showLegend={false} minCalloutPercent={0.01} maxCallouts={3} />
                  <DataTable columns={statusColumns} rows={designRestampStatus} />
                </div>
              )}
            </div>
          )}
          {designInHouseRows.length > 0 && (
            <div className="pdf-grid-two">
              <div className="pdf-workbook-block">
                <DataTable
                  columns={[
                    { key: 'in_house_vs_external', label: 'In House vs External' },
                    { key: 'initial_project_delivery', label: 'Initial Project Delivery', align: 'right', render: formatNumber },
                    { key: 'restamp_required', label: 'Restamp Required', align: 'right', render: formatNumber },
                    { key: 'revision_rate', label: 'Revision Rate', align: 'right', render: formatPercent },
                  ]}
                  rows={designInHouseRows}
                />
              </div>
              <ChartCard title="Design Engineer — Initial Project Delivery vs Restamp Required by In House vs External" data={designInHouseRows} xKey="in_house_vs_external" type="groupedBar" series={[{ key: 'initial_project_delivery', name: 'Initial Project Delivery' }, { key: 'restamp_required', name: 'Restamp Required' }]} height={330} />
            </div>
          )}
        </>
      )}

      {peRows.length > 0 && (
        <>
          <div className="section-heading section-bar soft-bar">
            <h3>PE Engineer Performance</h3>
            <span>{peRows.length} milestone row{peRows.length === 1 ? '' : 's'}</span>
          </div>
          {(peServiceRows.length > 0 || peOnTimeRowsWithData.length > 0) && (
            <div className="pdf-grid-two">
              {peServiceRows.length > 0 && <ChartCard title="PE Engineer — Service Type Mix" data={peServiceRows} xKey="service_type" type="donut" nameKey="service_type" valueKey="project_count" height={460} showLegend minCalloutPercent={0.02} maxCallouts={3} />}
              {peOnTimeRowsWithData.length > 0 && (
                <div className="pdf-workbook-block">
                  <DataTable columns={onTimeSummaryColumns} rows={peOnTimeRowsWithData} />
                  <ChartCard title="PE Engineer — On-Time vs Late by Milestone" data={peOnTimeRowsWithData} xKey="milestone_type" type="groupedBar" series={[{ key: 'on_time', name: 'On-Time' }, { key: 'late', name: 'Late' }]} height={330} />
                </div>
              )}
            </div>
          )}
          {(showPeInitialStatus || showPeRestampStatus) && (
            <div className="pdf-grid-two small-chart-grid">
              {showPeInitialStatus && (
                <div className="pdf-workbook-block">
                  <ChartCard title="PE Engineer — Initial Project Delivery: On-Time vs Late" data={peInitialStatus} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={320} showLegend={false} minCalloutPercent={0.01} maxCallouts={3} />
                  <DataTable columns={statusColumns} rows={peInitialStatus} />
                </div>
              )}
              {showPeRestampStatus && (
                <div className="pdf-workbook-block">
                  <ChartCard title="PE Engineer — Restamp Required: On-Time vs Late" data={peRestampStatus} xKey="status" type="pie" nameKey="status" valueKey="project_count" height={320} showLegend={false} minCalloutPercent={0.01} maxCallouts={3} />
                  <DataTable columns={statusColumns} rows={peRestampStatus} />
                </div>
              )}
            </div>
          )}
          {peInHouseRows.length > 0 && (
            <div className="pdf-grid-two">
              <div className="pdf-workbook-block">
                <DataTable
                  columns={[
                    { key: 'in_house_vs_external', label: 'In House vs External' },
                    { key: 'initial_project_delivery', label: 'Initial Project Delivery', align: 'right', render: formatNumber },
                    { key: 'restamp_required', label: 'Restamp Required', align: 'right', render: formatNumber },
                    { key: 'revision_rate', label: 'Revision Rate', align: 'right', render: formatPercent },
                  ]}
                  rows={peInHouseRows}
                />
              </div>
              <ChartCard title="PE Engineer — Initial Project Delivery vs Restamp Required by In House vs External" data={peInHouseRows} xKey="in_house_vs_external" type="groupedBar" series={[{ key: 'initial_project_delivery', name: 'Initial Project Delivery' }, { key: 'restamp_required', name: 'Restamp Required' }]} height={330} />
            </div>
          )}
        </>
      )}

      {idleRowsWithData.length > 0 && (
        <div className="pdf-grid-two">
          <div className="pdf-workbook-block">
            <div className="section-bar">Average Idle Time</div>
            <DataTable
              columns={[
                { key: 'milestone_type', label: 'Milestone Type' },
                { key: 'avg_ready_for_design', label: 'Avg Ready for Design (hrs)', align: 'right', render: (value) => Number(value || 0).toFixed(2) },
                { key: 'avg_ready_for_pe', label: 'Avg Ready for PE (hrs)', align: 'right', render: (value) => Number(value || 0).toFixed(2) },
                { key: 'request_count', label: 'Request Count', align: 'right', render: formatNumber },
              ]}
              rows={idleRowsWithData}
            />
          </div>
          <ChartCard title="Average Idle Time" data={idleRowsWithData} xKey="milestone_type" type="groupedBar" series={[{ key: 'avg_ready_for_design', name: 'Avg Ready for Design (hrs)' }, { key: 'avg_ready_for_pe', name: 'Avg Ready for PE (hrs)' }]} height={330} />
        </div>
      )}

      {assignmentPreviewRows.length > 0 && (
        <div className="pdf-workbook-block">
          <div className="section-bar">Assignment Detail</div>
          <DataTable columns={assignmentColumns} rows={assignmentPreviewRows} />
        </div>
      )}
    </section>
  );
}


const internalKpiColumns = [
  { key: 'team_member_name', label: 'Team Member' },
  { key: 'role', label: 'Role' },
  { key: 'output_quantity', label: 'Output Quantity' },
  { key: 'quality', label: 'Quality' },
  { key: 'turnaround_time', label: 'Turnaround Time' },
];

const internalAssignmentColumns = [
  { key: 'team_member_name', label: 'Team Member' },
  { key: 'role', label: 'Role' },
  { key: 'project_name', label: 'Project' },
  { key: 'milestone_date', label: 'Date' },
  { key: 'milestone_type', label: 'Milestone Type' },
  { key: 'assigned_role', label: 'Assigned Role' },
  { key: 'service_type', label: 'Service Type' },
  { key: 'tat_hours', label: 'TAT', align: 'right', render: (value) => value === null || value === undefined ? '—' : Number(value).toFixed(2) },
  { key: 'target_tat_label', label: 'Target TAT', align: 'right' },
  { key: 'qa_result', label: 'QA Result' },
  { key: 'cre_restamp_reason', label: 'CRE Restamp Reason' },
  { key: 'status', label: 'Role TAT Status' },
];

function internalKpiRow(member, role) {
  const output = member.kpiSummary.find((item) => item.key === 'output_quantity');
  const quality = member.kpiSummary.find((item) => item.key === 'quality');
  const tat = member.kpiSummary.find((item) => item.key === 'turnaround_time');
  return {
    team_member_name: member.display,
    role,
    output_quantity: formatKpiValue(output),
    quality: formatKpiValue(quality),
    turnaround_time: formatKpiValue(tat),
  };
}

function noAssignmentRow(member, role) {
  return {
    team_member_name: member.display,
    role,
    project_name: 'No assignment details found for the selected date range.',
    milestone_date: '',
    milestone_type: '',
    assigned_role: '',
    service_type: '',
    tat_hours: null,
    target_tat_label: '',
    qa_result: '',
    cre_restamp_reason: '',
    status: '',
  };
}



function selectedSectionCount(selection) {
  return Object.values(selection || {}).filter(Boolean).length;
}

function internalReportRows(rows) {
  const groups = buildInternalMemberReportRows(rows || []);
  const kpiRows = groups.flatMap((group) => group.members.map((member) => internalKpiRow(member, group.role)));
  const assignmentRows = groups.flatMap((group) => group.members.flatMap((member) => (
    member.assignments.length > 0 ? member.assignments : [noAssignmentRow(member, group.role)]
  )));
  return { groups, kpiRows, assignmentRows };
}


function downloadWorkbookBuffer(buffer, fileName) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function setCellBorder(cell) {
  cell.border = {
    top: { style: 'thin', color: { argb: 'B8C7D9' } },
    left: { style: 'thin', color: { argb: 'B8C7D9' } },
    bottom: { style: 'thin', color: { argb: 'B8C7D9' } },
    right: { style: 'thin', color: { argb: 'B8C7D9' } },
  };
}

function styleReportTitle(sheet, title, subtitle, activeUploadName, throughColumn) {
  sheet.mergeCells(`A1:${throughColumn}1`);
  const titleCell = sheet.getCell('A1');
  titleCell.value = title;
  titleCell.font = { bold: true, size: 22, color: { argb: '0F4F78' } };
  titleCell.alignment = { vertical: 'middle' };
  sheet.getRow(1).height = 32;

  sheet.mergeCells(`A2:${throughColumn}2`);
  const subtitleCell = sheet.getCell('A2');
  subtitleCell.value = subtitle;
  subtitleCell.font = { size: 11, color: { argb: '526071' } };

  sheet.mergeCells(`A3:${throughColumn}3`);
  const uploadCell = sheet.getCell('A3');
  uploadCell.value = activeUploadName ? `Active upload: ${activeUploadName}` : 'Active upload:';
  uploadCell.font = { size: 11, color: { argb: '526071' } };

  sheet.addRow([]);
}

function styleHeaderRow(row) {
  row.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0F4F78' } };
    cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 10 };
    cell.alignment = { vertical: 'middle', wrapText: true };
    setCellBorder(cell);
  });
  row.height = 24;
}

function styleBodyRow(row, rowIndex) {
  row.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: rowIndex % 2 === 0 ? 'FFFFFF' : 'F3F7FB' },
    };
    cell.font = { size: 10, color: { argb: '111827' } };
    cell.alignment = { vertical: 'top', wrapText: true };
    setCellBorder(cell);
  });
}

function addFormattedTable(sheet, title, headers, rows, startRow, options = {}) {
  const titleRow = sheet.getRow(startRow);
  titleRow.getCell(1).value = title;
  titleRow.getCell(1).font = { bold: true, size: 16, color: { argb: '0F4F78' } };
  titleRow.height = 24;

  const headerRow = sheet.getRow(startRow + 1);
  headers.forEach((header, index) => {
    headerRow.getCell(index + 1).value = header.label;
  });
  styleHeaderRow(headerRow);

  if (!rows.length) {
    const row = sheet.getRow(startRow + 2);
    row.getCell(1).value = 'No records found';
    row.getCell(1).font = { italic: true, color: { argb: '526071' } };
    row.height = 22;
    return startRow + 4;
  }

  rows.forEach((item, itemIndex) => {
    const row = sheet.getRow(startRow + 2 + itemIndex);
    headers.forEach((header, index) => {
      const cell = row.getCell(index + 1);
      cell.value = item[header.key] ?? '';
      if (header.numFmt) cell.numFmt = header.numFmt;
      if (header.align) cell.alignment = { vertical: 'top', horizontal: header.align, wrapText: true };
    });
    styleBodyRow(row, itemIndex);
  });

  const tableStart = startRow + 1;
  const tableEnd = startRow + 1 + rows.length;
  sheet.autoFilter = {
    from: { row: tableStart, column: 1 },
    to: { row: tableEnd, column: headers.length },
  };
  return tableEnd + 3;
}

function applyColumnWidths(sheet, widths) {
  widths.forEach((width, index) => {
    sheet.getColumn(index + 1).width = width;
  });
}

async function exportInternalTeamKpiXlsx(rows, { startDate, endDate, activeUpload }) {
  const { kpiRows, assignmentRows } = internalReportRows(rows || []);
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Operations KPI Dashboard';
  workbook.created = new Date();

  const subtitle = `${startDate || ''} to ${endDate || ''}`;
  const activeUploadName = activeUpload?.file_name || '';

  const summary = workbook.addWorksheet('Summary', { views: [{ showGridLines: false }] });
  styleReportTitle(summary, 'Internal Team KPI Report', subtitle, activeUploadName, 'E');
  const summaryRows = [
    ['Report', 'Internal Team KPI Report'],
    ['Date Range', subtitle],
    ['Active Upload', activeUploadName],
    ['Generated At', new Date().toLocaleString()],
  ];
  summaryRows.forEach((rowData, index) => {
    const row = summary.getRow(5 + index);
    row.values = rowData;
    row.getCell(1).font = { bold: true, color: { argb: '0F4F78' } };
    row.getCell(2).font = { color: { argb: '111827' } };
    row.eachCell(setCellBorder);
  });
  applyColumnWidths(summary, [22, 70]);

  const kpiSheet = workbook.addWorksheet('KPI', {
    views: [{ state: 'frozen', ySplit: 6, showGridLines: false }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  styleReportTitle(kpiSheet, 'Internal Team KPI Report', subtitle, activeUploadName, 'E');
  addFormattedTable(
    kpiSheet,
    'KPI',
    [
      { key: 'team_member_name', label: 'Team Member' },
      { key: 'role', label: 'Role' },
      { key: 'output_quantity', label: 'Output Quantity', align: 'right' },
      { key: 'quality', label: 'Quality', align: 'right' },
      { key: 'turnaround_time', label: 'Turnaround Time', align: 'right' },
    ],
    kpiRows,
    5
  );
  applyColumnWidths(kpiSheet, [28, 10, 18, 14, 18]);
  kpiSheet.getColumn(1).font = { bold: true };

  const assignmentSheet = workbook.addWorksheet('Assignment Detail', {
    views: [{ state: 'frozen', ySplit: 6, showGridLines: false }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  styleReportTitle(assignmentSheet, 'Internal Team KPI Report', subtitle, activeUploadName, 'L');
  addFormattedTable(
    assignmentSheet,
    'Assignment Detail',
    [
      { key: 'team_member_name', label: 'Team Member' },
      { key: 'role', label: 'Role' },
      { key: 'project_name', label: 'Project' },
      { key: 'milestone_date', label: 'Date' },
      { key: 'milestone_type', label: 'Milestone Type' },
      { key: 'assigned_role', label: 'Assigned Role' },
      { key: 'service_type', label: 'Service Type' },
      { key: 'tat_hours', label: 'TAT', align: 'right', numFmt: '0.00' },
      { key: 'target_tat_label', label: 'Target TAT', align: 'right' },
      { key: 'qa_result', label: 'Milestone QA Result' },
      { key: 'cre_restamp_reason', label: 'CRE Restamp Reason' },
      { key: 'status', label: 'Role TAT Status' },
    ],
    assignmentRows.map((row) => ({
      ...row,
      tat_hours: row.tat_hours === null || row.tat_hours === undefined ? '' : Number(row.tat_hours),
    })),
    5
  );
  applyColumnWidths(assignmentSheet, [24, 10, 34, 12, 22, 18, 18, 10, 12, 20, 34, 16]);
  assignmentSheet.getColumn(1).font = { bold: true };
  assignmentSheet.getColumn(11).alignment = { wrapText: true, vertical: 'top' };

  [summary, kpiSheet, assignmentSheet].forEach((sheet) => {
    sheet.properties.defaultRowHeight = 18;
    sheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.alignment = { ...(cell.alignment || {}), vertical: 'middle', wrapText: true };
      });
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadWorkbookBuffer(buffer, `internal-team-kpi-report-${startDate}-to-${endDate}.xlsx`);
}

function safePdfValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return Number.isFinite(value) ? String(Math.round(value * 100) / 100) : '—';
  return String(value);
}

function valueForPdfColumn(row, column) {
  const raw = row?.[column.key];
  if (column.key === 'tat_hours') {
    return raw === null || raw === undefined || raw === '' ? '—' : Number(raw).toFixed(2);
  }
  if (column.render && column.key !== 'tat_hours') {
    try {
      return safePdfValue(column.render(raw, row));
    } catch (error) {
      return safePdfValue(raw);
    }
  }
  return safePdfValue(raw);
}

function drawPdfTable(pdf, title, columns, rows, options = {}) {
  const pageWidth = options.pageWidth || 1900;
  const pageHeight = options.pageHeight || 1200;
  const margin = options.margin || 42;
  const headerFill = options.headerFill || [15, 79, 120];
  const headerText = [255, 255, 255];
  const border = [190, 204, 220];
  const text = [17, 24, 39];
  const muted = [82, 96, 113];
  const widths = columns.map((column) => column.pdfWidth || column.width || 120);
  const usableWidth = pageWidth - margin * 2;
  const totalWidth = widths.reduce((sum, width) => sum + width, 0);
  const scale = totalWidth > usableWidth ? usableWidth / totalWidth : 1;
  const scaledWidths = widths.map((width) => Math.floor(width * scale));
  let y = options.startY ?? margin;

  function addFreshPage() {
    pdf.addPage([pageWidth, pageHeight], pageWidth >= pageHeight ? 'landscape' : 'portrait');
    y = margin;
  }

  function ensureSpace(height) {
    if (y + height > pageHeight - margin) addFreshPage();
  }

  function drawTitle() {
    ensureSpace(48);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(options.titleFontSize || 16);
    pdf.setTextColor(...headerFill);
    pdf.text(title, margin, y + 18);
    y += 34;
  }

  function drawHeader() {
    const headerHeight = options.headerHeight || 34;
    ensureSpace(headerHeight + 12);
    let x = margin;
    pdf.setDrawColor(...border);
    columns.forEach((column, index) => {
      const width = scaledWidths[index];
      // Set fill/text for every cell so jsPDF never inherits a body style
      // and leaves later header cells white.
      pdf.setFillColor(...headerFill);
      pdf.rect(x, y, width, headerHeight, 'FD');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(options.headerFontSize || 8);
      pdf.setTextColor(...headerText);
      const lines = pdf.splitTextToSize(column.label, Math.max(20, width - 8));
      pdf.text(lines.slice(0, 2), x + 4, y + 12);
      x += width;
    });
    y += headerHeight;
  }

  drawTitle();
  drawHeader();

  const tableRows = rows && rows.length ? rows : [{ __empty: true }];
  tableRows.forEach((row, rowIndex) => {
    const isEmpty = row.__empty;
    const cellLines = columns.map((column, index) => {
      const width = scaledWidths[index];
      const value = isEmpty ? (index === 0 ? 'No records found' : '') : valueForPdfColumn(row, column);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(options.bodyFontSize || 8);
      return pdf.splitTextToSize(value, Math.max(20, width - 8));
    });
    const maxLines = Math.max(1, ...cellLines.map((lines) => lines.length));
    const rowHeight = Math.max(options.minRowHeight || 26, maxLines * (options.lineHeight || 10) + 10);
    ensureSpace(rowHeight);
    if (y === margin) drawHeader();

    let x = margin;
    const fill = rowIndex % 2 === 0 ? [255, 255, 255] : [247, 250, 252];
    columns.forEach((column, index) => {
      const width = scaledWidths[index];
      pdf.setFillColor(...fill);
      pdf.setDrawColor(...border);
      pdf.rect(x, y, width, rowHeight, 'FD');
      pdf.setFont('helvetica', column.bold ? 'bold' : 'normal');
      pdf.setFontSize(options.bodyFontSize || 8);
      pdf.setTextColor(...(isEmpty ? muted : text));
      const lines = cellLines[index];
      const textX = x + 4;
      const textY = y + 12;
      if (column.align === 'right') {
        pdf.text(lines, x + width - 4, textY, { align: 'right' });
      } else {
        pdf.text(lines, textX, textY);
      }
      x += width;
    });
    y += rowHeight;
  });

  return y;
}

const internalKpiPdfColumns = [
  { key: 'team_member_name', label: 'Team Member', pdfWidth: 210, bold: true },
  { key: 'role', label: 'Role', pdfWidth: 88 },
  { key: 'output_quantity', label: 'Output Quantity', pdfWidth: 112 },
  { key: 'quality', label: 'Quality', pdfWidth: 90 },
  { key: 'turnaround_time', label: 'Turnaround Time', pdfWidth: 112 },
];

const internalAssignmentPdfColumns = [
  { key: 'team_member_name', label: 'Team Member', pdfWidth: 170, bold: true },
  { key: 'role', label: 'Role', pdfWidth: 92 },
  { key: 'project_name', label: 'Project', pdfWidth: 235 },
  { key: 'milestone_date', label: 'Date', pdfWidth: 88 },
  { key: 'milestone_type', label: 'Milestone Type', pdfWidth: 162 },
  { key: 'assigned_role', label: 'Assigned Role', pdfWidth: 142 },
  { key: 'service_type', label: 'Service Type', pdfWidth: 135 },
  { key: 'tat_hours', label: 'TAT', pdfWidth: 72, align: 'right' },
  { key: 'target_tat_label', label: 'Target TAT', pdfWidth: 92, align: 'right' },
  { key: 'qa_result', label: 'QA Result', pdfWidth: 115 },
  { key: 'cre_restamp_reason', label: 'CRE Restamp Reason', pdfWidth: 245 },
  { key: 'status', label: 'Role TAT Status', pdfWidth: 116 },
];

function appendInternalTeamKpiPdfPages(pdf, rows, { startDate, endDate, activeUpload, appendToExisting = false } = {}) {
  const pageWidth = 1900;
  const pageHeight = 1200;
  const margin = 42;
  // jsPDF creates one blank page by default. Only add a page when appending
  // this report after already-rendered dashboard pages. This prevents a blank
  // first page when exporting the Internal Team KPI Report by itself.
  if (appendToExisting) {
    pdf.addPage([pageWidth, pageHeight], 'landscape');
  }

  const { kpiRows, assignmentRows } = internalReportRows(rows || []);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(22);
  pdf.setTextColor(15, 79, 120);
  pdf.text('Internal Team KPI Report', margin, 48);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  pdf.setTextColor(82, 96, 113);
  pdf.text(`${startDate || ''} to ${endDate || ''}`, margin, 70);
  if (activeUpload?.file_name) {
    pdf.text(`Active upload: ${activeUpload.file_name}`, margin, 88);
  }

  const afterKpi = drawPdfTable(pdf, 'KPI', internalKpiPdfColumns, kpiRows, {
    pageWidth,
    pageHeight,
    margin,
    startY: 116,
    titleFontSize: 15,
    headerFontSize: 8,
    bodyFontSize: 8,
    minRowHeight: 24,
    lineHeight: 9,
  });

  drawPdfTable(pdf, 'Assignment Detail', internalAssignmentPdfColumns, assignmentRows, {
    pageWidth,
    pageHeight,
    margin,
    startY: Math.min(afterKpi + 36, pageHeight - 180),
    titleFontSize: 15,
    headerFontSize: 7,
    bodyFontSize: 7,
    minRowHeight: 24,
    lineHeight: 8,
  });
}

function saveInternalTeamKpiPdf(rows, { startDate, endDate, activeUpload } = {}) {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [1900, 1200], compress: true, hotfixes: ['px_scaling'] });
  appendInternalTeamKpiPdfPages(pdf, rows, { startDate, endDate, activeUpload });
  pdf.save(`internal-team-kpi-report-${startDate}-to-${endDate}.pdf`);
}

function PdfInternalTeamKpiSection({ rows }) {
  const { kpiRows, assignmentRows } = useMemo(() => internalReportRows(rows || []), [rows]);
  return (
    <section className="pdf-section pdf-internal-team-section">
      <h2>Internal Team KPI Report</h2>
      <p className="pdf-section-note">Internal team members are ordered by role: PE I, PE II, then SPVD. The KPI table is separate from Assignment Detail.</p>
      <div className="pdf-workbook-block internal-report-table-block internal-kpi-table-block">
        <div className="section-bar">KPI</div>
        <DataTable columns={internalKpiColumns} rows={kpiRows} />
      </div>
      <div className="pdf-workbook-block internal-report-table-block">
        <div className="section-bar">Assignment Detail</div>
        <DataTable columns={internalAssignmentColumns} rows={assignmentRows} />
      </div>
    </section>
  );
}

function PdfStateClientSection({ stateRows, clientRows }) {
  return (
    <section className="pdf-section pdf-state-client-section">
      <h2>State & Client Summary</h2>
      <div className="pdf-grid-two">
        <div className="pdf-workbook-block">
          <div className="section-bar">Projects per State</div>
          <DataTable columns={[{ key: 'state', label: 'State' }, { key: 'project_count', label: 'Project Count', align: 'right' }]} rows={stateRows} />
        </div>
        <ChartCard title="Projects per State" data={stateRows} xKey="state" height={categoryChartHeight(stateRows.length)} showLegend={false} />
      </div>
      <div className="pdf-grid-two">
        <div className="pdf-workbook-block">
          <div className="section-bar">Projects per Client</div>
          <DataTable columns={[{ key: 'client', label: 'Client Name' }, { key: 'project_count', label: 'Project Count', align: 'right' }]} rows={clientRows} />
        </div>
        <ChartCard title="Projects per Client" data={clientRows} xKey="client" height={categoryChartHeight(clientRows.length)} showLegend={false} />
      </div>
    </section>
  );
}

function PdfReport({ data, selection, startDate, endDate, activeUpload }) {
  return (
    <div className="pdf-report-page">
      <div className="pdf-report-header">
        <div>
          <h1>Operations KPI Dashboard</h1>
          <p>{startDate} to {endDate}</p>
        </div>
        <div>
          <strong>{activeUpload?.file_name || 'No active upload'}</strong>
          <span>{activeUpload?.uploaded_at ? new Date(activeUpload.uploaded_at).toLocaleDateString() : ''}</span>
        </div>
      </div>
      {selection.dashboard && <PdfDashboardSection summary={data.dashboard} />}
      {selection.team && <PdfTeamSection teamRows={data.teamRows || []} />}
      {selection.individual && <PdfIndividualTeamMemberSection rows={data.individualRows || []} selectedMember={data.selectedMember} tatSettings={data.tatSettings} />}
      {selection.internalTeam && <PdfInternalTeamKpiSection rows={data.internalTeamRows || []} />}
      {selection.stateClient && <PdfStateClientSection stateRows={data.stateRows || []} clientRows={data.clientRows || []} />}
      <div className="pdf-report-footer">Generated from the active Project Milestone Report. Page size is automatically expanded so all selected tables and charts fit on one PDF page.</div>
    </div>
  );
}

export default function PdfExportButton() {
  const { activeUpload } = useActiveUpload();
  const { startDate, endDate } = useDateRange();
  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState(DEFAULT_SELECTION);
  const [renderSelection, setRenderSelection] = useState(DEFAULT_SELECTION);
  const [data, setData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberRows, setMemberRows] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [error, setError] = useState('');
  const reportRef = useRef(null);

  function toggle(key) {
    setSelection((current) => ({ ...current, [key]: !current[key] }));
  }

  useEffect(() => {
    if (!open || !selection.individual || !activeUpload?.id) return undefined;
    let cancelled = false;
    async function loadMembers() {
      setLoadingMembers(true);
      setError('');
      try {
        const rows = await getTeamMemberDetailRows(activeUpload.id, startDate, endDate);
        if (cancelled) return;
        const options = buildMemberOptions(rows || []);
        setMemberRows(rows || []);
        setMemberOptions(options);
        setSelectedMember((current) => {
          if (current && options.some((name) => memberKey(name) === memberKey(current))) return current;
          return options[0] || '';
        });
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    }
    loadMembers();
    return () => { cancelled = true; };
  }, [open, selection.individual, activeUpload?.id, startDate, endDate]);

  async function loadSelectedData() {
    if (!activeUpload?.id) throw new Error('No active upload is available.');
    const next = {};
    if (selection.dashboard) {
      next.dashboard = await getDashboardSummary(activeUpload.id, startDate, endDate);
    }
    if (selection.team) {
      next.teamRows = await getTeamMemberPerformance(activeUpload.id, startDate, endDate);
    }
    if (selection.individual) {
      const rows = memberRows.length ? memberRows : await getTeamMemberDetailRows(activeUpload.id, startDate, endDate);
      const options = memberOptions.length ? memberOptions : buildMemberOptions(rows || []);
      const member = selectedMember || options[0];
      if (!member) throw new Error('No team members are available for the selected date range.');
      const settings = await getTatSettings();
      next.individualRows = rows || [];
      next.selectedMember = member;
      next.tatSettings = settings;
    }
    if (selection.internalTeam) {
      const rows = await getTeamMemberDetailRows(activeUpload.id, startDate, endDate);
      next.internalTeamRows = rows || [];
      next.tatSettings = next.tatSettings || await getTatSettings();
    }
    if (selection.stateClient) {
      const [states, clients] = await Promise.all([
        getStateSummary(activeUpload.id, startDate, endDate),
        getClientSummary(activeUpload.id, startDate, endDate),
      ]);
      next.stateRows = states.map((row) => ({ ...row, project_count: Number(row.project_count || 0) }));
      next.clientRows = clients.map((row) => ({ ...row, project_count: Number(row.project_count || 0) }));
    }
    return next;
  }


  async function handleInternalTeamExcelDownload() {
    setGenerating(true);
    setError('');
    try {
      if (!activeUpload?.id) throw new Error('No active upload is available.');
      const rows = await getTeamMemberDetailRows(activeUpload.id, startDate, endDate);
      await exportInternalTeamKpiXlsx(rows || [], { startDate, endDate, activeUpload });
      setOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  async function handleDownload() {
    setGenerating(true);
    setError('');
    try {
      if (!Object.values(selection).some(Boolean)) throw new Error('Choose at least one section to include.');
      const nextData = await loadSelectedData();
      const onlyInternalTeam = selection.internalTeam && selectedSectionCount(selection) === 1;
      if (onlyInternalTeam) {
        saveInternalTeamKpiPdf(nextData.internalTeamRows || [], { startDate, endDate, activeUpload });
        setOpen(false);
        return;
      }

      const nextRenderSelection = selection.internalTeam ? { ...selection, internalTeam: false } : selection;
      setRenderSelection(nextRenderSelection);
      setData(nextData);
      await wait(1400);
      if (!reportRef.current) throw new Error('PDF report layout was not ready. Please try again.');

      const reportNode = reportRef.current;
      const bounds = reportNode.getBoundingClientRect();
      const fullWidth = Math.ceil(Math.max(reportNode.scrollWidth, reportNode.offsetWidth, bounds.width) + 120);
      const fullHeight = Math.ceil(Math.max(reportNode.scrollHeight, reportNode.offsetHeight, bounds.height) + 120);
      const canvas = await html2canvas(reportNode, {
        scale: 1.35,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        width: fullWidth,
        height: fullHeight,
        windowWidth: fullWidth + 240,
        windowHeight: fullHeight + 240,
        scrollX: 0,
        scrollY: 0,
        x: 0,
        y: 0,
        onclone: (clonedDocument) => {
          const clonedHost = clonedDocument.querySelector('.pdf-render-host');
          if (clonedHost) {
            clonedHost.style.left = '0px';
            clonedHost.style.top = '0px';
            clonedHost.style.width = `${fullWidth}px`;
            clonedHost.style.overflow = 'visible';
          }
          const clonedPage = clonedDocument.querySelector('.pdf-report-page');
          if (clonedPage) {
            clonedPage.style.width = `${fullWidth - 80}px`;
            clonedPage.style.overflow = 'visible';
          }
        },
      });

      const pageWidth = canvas.width;
      const pageHeight = canvas.height;
      const orientation = pageWidth >= pageHeight ? 'landscape' : 'portrait';
      const pdf = new jsPDF({ orientation, unit: 'px', format: [pageWidth, pageHeight], compress: true, hotfixes: ['px_scaling'] });
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, pageWidth, pageHeight);
      if (selection.internalTeam) {
        appendInternalTeamKpiPdfPages(pdf, nextData.internalTeamRows || [], { startDate, endDate, activeUpload, appendToExisting: true });
      }
      pdf.save(`operations-kpi-dashboard-${startDate}-to-${endDate}.pdf`);
      setOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <>
      <button type="button" className="button secondary" onClick={() => setOpen(true)} disabled={!activeUpload}>
        Download PDF
      </button>
      {open && (
        <div className="modal-backdrop" role="presentation">
          <div className="modal-card" role="dialog" aria-modal="true" aria-label="Download PDF">
            <div className="modal-header">
              <div>
                <h2>Download single-page PDF</h2>
                <p>Select the dashboard sections to include.</p>
              </div>
              <button type="button" className="link-button" onClick={() => setOpen(false)}>Close</button>
            </div>
            <ErrorMessage message={error} />
            <div className="checkbox-stack">
              <label><input type="checkbox" checked={selection.dashboard} onChange={() => toggle('dashboard')} /> Dashboard</label>
              <label><input type="checkbox" checked={selection.team} onChange={() => toggle('team')} /> Team Member Performance</label>
              <label><input type="checkbox" checked={selection.individual} onChange={() => toggle('individual')} /> Individual Team Member Performance</label>
              {selection.individual && (
                <div className="pdf-member-picker">
                  {loadingMembers ? (
                    <LoadingState message="Loading team members..." />
                  ) : (
                    <label>
                      <span>Team Member for PDF</span>
                      <select value={selectedMember} onChange={(event) => setSelectedMember(event.target.value)} disabled={!memberOptions.length}>
                        {!memberOptions.length && <option value="">No team members found</option>}
                        {memberOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                      </select>
                    </label>
                  )}
                </div>
              )}
              <label><input type="checkbox" checked={selection.internalTeam} onChange={() => toggle('internalTeam')} /> Internal Team KPI Report</label>
              <label><input type="checkbox" checked={selection.stateClient} onChange={() => toggle('stateClient')} /> State & Client Summary</label>
            </div>
            <p className="section-note">The PDF uses a custom oversized page instead of A4/Letter, so all selected tables and charts are captured without clipping or missing parts.</p>
            <div className="modal-actions">
              <button type="button" className="button ghost" onClick={() => setOpen(false)}>Cancel</button>
              {selection.internalTeam && (
                <button type="button" className="button secondary" onClick={handleInternalTeamExcelDownload} disabled={generating || loadingMembers}>
                  {generating ? 'Preparing Excel...' : 'Export Internal Team Excel'}
                </button>
              )}
              <button type="button" className="button primary" onClick={handleDownload} disabled={generating || loadingMembers}>
                {generating ? 'Preparing PDF...' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
      {data && (
        <div className="pdf-render-host" aria-hidden="true">
          <div ref={reportRef}>
            <PdfReport data={data} selection={renderSelection} startDate={startDate} endDate={endDate} activeUpload={activeUpload} />
          </div>
        </div>
      )}
    </>
  );
}
